import { formatDate } from "@plasma/utils/time";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import { locationSalesGetRefundData, type RefundEntry } from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedRefund = RefundEntry;

interface RefundsViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const RefundsView = ({
  locationId,
  dateRange,
  tableId,
}: RefundsViewProps) => {
  const data =
    useQuery(locationSalesGetRefundData, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isLoading = data === undefined;

  const columnHelper = createColumnHelper<ExtendedRefund>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("orderCreatedAt", {
          header: "Date of Trans.",
          cell: ({ getValue }) => formatDate(getValue(), "MMM D, YYYY"),
          meta: {
            getCellForExport: (d: ExtendedRefund["orderCreatedAt"]) =>
              d.toISOString(),
          },
        }),
        columnHelper.accessor("billId", {
          header: "Bill ID",
        }),
        columnHelper.accessor("orderId", {
          header: "Order ID",
        }),
        columnHelper.accessor("employee", {
          header: "Employee Name",
          cell: ({ getValue }) => {
            const employee = getValue();
            if (!employee) {
              return "N/A";
            }
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.employee
              ? filterValue.includes(original.employee.id)
              : false,
          meta: {
            getCellForExport: (e: ExtendedRefund["employee"]) =>
              e ? `${e.firstName} ${e.lastName}` : "N/A",
            faceting: {
              title: "Employee",
              getOption: (e: ExtendedRefund["employee"]) => ({
                value: e?.id ?? "",
                label: e ? `${e.firstName} ${e.lastName}` : "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("oldTotal", {
          header: "Original Bill Total",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: ExtendedRefund["oldTotal"]) => d,
          },
        }),
        columnHelper.accessor("refundAmount", {
          header: "Refund Amount",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: ExtendedRefund["refundAmount"]) => d,
          },
        }),
        columnHelper.accessor("refundMethod", {
          header: "Refund Method",
        }),
        columnHelper.accessor("newTotal", {
          header: "New Bill Total",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: ExtendedRefund["newTotal"]) => d,
          },
        }),
      ] as ColumnDef<ExtendedRefund>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading refund data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
